import { DateTime } from "luxon";
import { isNotUndefined } from "../helpers/utilities.js";
import {
  reconcileCourse
} from "./course.js";
import {
  buildSectionDefinitionsById,
  getInterviewSectionTopic
} from "./interviewSection/definition.js";
import {
  buildSectionResultsById,
  compareInterviewSectionStatus,
  findMostCompletedSectionResult,
  InterviewSectionStatus
} from "./interviewSection/result.js";
import { LessonOrder } from "./jobType.js";
const LessonStatus = {
  /** Unavailable because prerequisites are not met. */
  Unavailable: "unavailable",
  /** Available but not yet started. */
  Pending: InterviewSectionStatus.Pending,
  /** Started but not yet completed. */
  InProgress: InterviewSectionStatus.InProgress,
  /** Submitted by the participant and waiting to be processed. */
  Submitted: InterviewSectionStatus.Submitted,
  /** Processed and determined to have been completed. */
  Completed: InterviewSectionStatus.Completed
};
const allLessonStatuses = [
  LessonStatus.Unavailable,
  LessonStatus.Pending,
  LessonStatus.InProgress,
  LessonStatus.Submitted,
  LessonStatus.Completed
];
function compareLessonStatus(a, b) {
  return allLessonStatuses.indexOf(a) - allLessonStatuses.indexOf(b);
}
function checkCanRestartLesson(status, isRetakeAllowed) {
  switch (status) {
    case LessonStatus.Unavailable:
    case LessonStatus.Pending:
      return false;
    case LessonStatus.InProgress:
    case LessonStatus.Submitted:
      return true;
    case LessonStatus.Completed:
      return isRetakeAllowed;
  }
}
const ChapterStatus = {
  /** All lessons are unavailable. */
  Unavailable: LessonStatus.Unavailable,
  /** Some lessons are available but none have been started. */
  Pending: LessonStatus.Pending,
  /** Some lessons have been started but not all lessons have been completed. */
  InProgress: LessonStatus.InProgress,
  /** All lessons have been submitted but not all lessons have been processed. */
  Submitted: LessonStatus.Submitted,
  /** All lessons have been completed. */
  Completed: LessonStatus.Completed
};
function computeTrainingParticipant(projectName, jobTypeId, courseFromJobType, lessonOrder, interview, sectionDefinitions, sectionResults) {
  const course = computeTrainingParticipantCourse(
    projectName,
    courseFromJobType,
    lessonOrder,
    sectionDefinitions,
    sectionResults
  );
  const lessonActivityDates = course.chapters.flatMap(
    (chapter) => chapter.lessons.map((lesson) => lesson.lastActivityAt)
  ).filter((iso) => iso !== null).map((iso) => DateTime.fromISO(iso));
  return {
    job_type_id: jobTypeId,
    project_id: interview.project_id,
    id: interview.id,
    contact: interview.contact,
    course,
    assessment: interview.metadata?.training_participant_assessment ?? null,
    last_activity_at: DateTime.max(...lessonActivityDates)?.toISO() ?? interview.created_at
  };
}
function computeTrainingParticipantCourse(projectName, courseFromJobType, lessonOrder, sectionDefinitions, sectionResults) {
  const course = reconcileCourse(courseFromJobType, sectionDefinitions);
  const sectionDefinitionsById = buildSectionDefinitionsById(sectionDefinitions);
  const sectionResultsById = buildSectionResultsById(sectionResults);
  let isEverythingCompletedSoFar = true;
  const computeLessonStatus = (sectionStatus) => {
    const isThisCompleted = sectionStatus === InterviewSectionStatus.Completed;
    const isEverythingBeforeThisCompleted = isEverythingCompletedSoFar;
    isEverythingCompletedSoFar = isEverythingCompletedSoFar && isThisCompleted;
    const isThisAtLeastInProgress = compareInterviewSectionStatus(
      InterviewSectionStatus.InProgress,
      sectionStatus
    ) <= 0;
    if (isThisAtLeastInProgress) {
      return sectionStatus;
    }
    if (lessonOrder === LessonOrder.Any) {
      return LessonStatus.Pending;
    }
    if (isEverythingBeforeThisCompleted) {
      return LessonStatus.Pending;
    }
    return LessonStatus.Unavailable;
  };
  return {
    name: projectName,
    chapters: course.chapters.map(
      (chapter, chapterIndex) => {
        const lessons = chapter.lessons.map((lesson) => {
          const sectionDefinition = sectionDefinitionsById.get(
            lesson.interviewSectionDefinitionId
          );
          if (!sectionDefinition) {
            return void 0;
          }
          const sectionResult = findMostCompletedSectionResult(
            sectionResultsById,
            lesson.interviewSectionDefinitionId
          );
          const rawSectionStatus = sectionResult?.status ?? InterviewSectionStatus.Pending;
          const sectionStatus = rawSectionStatus === InterviewSectionStatus.Canceled ? InterviewSectionStatus.Pending : rawSectionStatus;
          return {
            id: lesson.interviewSectionDefinitionId,
            interviewSectionDefinitionId: lesson.interviewSectionDefinitionId,
            completedInterviewSectionResultId: sectionResult?.status === InterviewSectionStatus.Completed ? sectionResult.id : null,
            type: sectionDefinition.settings.type,
            topic: getInterviewSectionTopic(sectionDefinition.settings),
            status: computeLessonStatus(sectionStatus),
            lastActivityAt: sectionResult?.updated_at ?? sectionResult?.created_at ?? null
          };
        }).filter(isNotUndefined);
        return {
          id: chapter.id,
          topic: chapter.topic,
          status: computeChapterStatus(lessons),
          lessons
        };
      }
    )
  };
}
function computeMostCompletedSectionStatus(sectionResultsById, interviewSectionDefinitionId) {
  const mostCompletedSectionResult = findMostCompletedSectionResult(
    sectionResultsById,
    interviewSectionDefinitionId
  );
  return mostCompletedSectionResult?.status ?? InterviewSectionStatus.Pending;
}
function computeChapterStatus(lessons) {
  if (lessons.length === 0) {
    return ChapterStatus.Unavailable;
  }
  const [leastCompletedLessonStatus, mostCompletedLessonStatus] = lessons.reduce(
    ([minLessonStatus, maxLessonStatus], lesson) => [
      compareLessonStatus(minLessonStatus, lesson.status) < 0 ? minLessonStatus : lesson.status,
      compareLessonStatus(maxLessonStatus, lesson.status) > 0 ? maxLessonStatus : lesson.status
    ],
    [LessonStatus.Completed, LessonStatus.Unavailable]
  );
  if (leastCompletedLessonStatus === LessonStatus.Completed) {
    return ChapterStatus.Completed;
  }
  if (leastCompletedLessonStatus === LessonStatus.Submitted) {
    return ChapterStatus.Submitted;
  }
  if (mostCompletedLessonStatus === LessonStatus.Unavailable) {
    return ChapterStatus.Unavailable;
  }
  if (mostCompletedLessonStatus === LessonStatus.Pending) {
    return ChapterStatus.Pending;
  }
  return ChapterStatus.InProgress;
}
export {
  ChapterStatus,
  LessonStatus,
  checkCanRestartLesson,
  compareLessonStatus,
  computeChapterStatus,
  computeMostCompletedSectionStatus,
  computeTrainingParticipant,
  computeTrainingParticipantCourse
};
//# sourceMappingURL=trainingParticipant.js.map
